import { useEffect, useState } from "react";
import { Alert, Button, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  isImpersonationActive,
  isApplicantImpersonationFlow,
  decodeAccessPayload,
  restoreImpersonationBackup,
  applyAuthSessionToStorage,
  getImpersonationBackup,
  removeImpersonationBackup,
  notifyImpersonationChanged,
  stopApplicantImpersonate,
  IMPERSONATION_CHANGED_EVENT,
} from "utils/impersonationAccess";

function ImpersonationBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState("");
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const sync = () => {
      const token = sessionStorage.getItem("token");
      if (!isImpersonationActive(token) || !isApplicantImpersonationFlow(token)) {
        setVisible(false);
        setLabel("");
        return;
      }
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      const payload = decodeAccessPayload(token);
      const roleName = user?.role || "Applicant";
      const actorEmail = payload?.impersonation?.actor_email || "";
      const base = `${user?.name || user?.email || "Applicant"} (${roleName})`;
      setLabel(actorEmail ? `${base} — actor: ${actorEmail}` : base);
      setVisible(true);
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(IMPERSONATION_CHANGED_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(IMPERSONATION_CHANGED_EVENT, sync);
    };
  }, []);

  const handleExit = async () => {
    setExiting(true);
    try {
      const backup = getImpersonationBackup();
      try {
        const data = await stopApplicantImpersonate();
        applyAuthSessionToStorage(data);
      } catch (apiErr) {
        if (backup) {
          restoreImpersonationBackup(backup);
          toast.warn("Restored your session from backup.");
        } else {
          throw apiErr;
        }
      }
      removeImpersonationBackup();
      notifyImpersonationChanged();
      toast.success("Exited impersonation");
      navigate("/search_applications");
    } catch (err) {
      toast.error(err.message || "Failed to exit impersonation");
    } finally {
      setExiting(false);
    }
  };

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9998,
      }}
    >
      <Alert
        severity="warning"
        variant="filled"
        action={
          <Button color="inherit" size="small" onClick={handleExit} disabled={exiting}>
            Exit impersonation
          </Button>
        }
        sx={{
          boxShadow: 2,
          py: 0.25,
          minHeight: 36,
          borderRadius: 0,
          fontSize: "0.8rem",
          "& .MuiAlert-message": { py: 0.25 },
        }}
      >
        Viewing as {label}
      </Alert>
    </Box>
  );
}

export default ImpersonationBanner;
